import { api } from './api';

// Generate or retrieve a unique device ID
const getDeviceId = () => {
  let deviceId = localStorage.getItem('taptile_device_id');
  if (!deviceId) {
    // Generate a unique device ID using crypto API
    deviceId = 'device_' + crypto.randomUUID();
    localStorage.setItem('taptile_device_id', deviceId);
  }
  return deviceId;
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return false;
  }

  if (!('serviceWorker' in navigator)) {
    console.log('This browser does not support service workers');
    return false;
  }

  const permission = await Notification.requestPermission();
  return permission === 'granted';
};

export const subscribeToPushNotifications = async () => {
  try {
    console.log('[Push] Starting subscription process...');
    
    // Request permission first
    const permissionGranted = await requestNotificationPermission();
    if (!permissionGranted) {
      console.log('[Push] Notification permission denied');
      return false;
    }
    console.log('[Push] Permission granted');

    // Register service worker if not already registered with timeout
    let registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      console.log('[Push] Registering service worker...');
      registration = await navigator.serviceWorker.register('/service-worker.js');
      
      // Wait for service worker to be ready with timeout
      const swReady = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Service worker registration timed out')), 10000)
        )
      ]);
      registration = swReady;
    }
    console.log('[Push] Service worker ready');

    // Get VAPID public key from backend
    console.log('[Push] Fetching VAPID public key...');
    const { publicKey } = await api.getVapidPublicKey();
    if (!publicKey) {
      throw new Error('No VAPID public key received from server');
    }
    console.log('[Push] VAPID key received:', publicKey?.substring(0, 20) + '...');
    
    // Subscribe to push notifications with timeout
    console.log('[Push] Subscribing to push manager...');
    const subscription = await Promise.race([
      registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      }),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Push subscription timed out')), 15000)
      )
    ]);
    console.log('[Push] Push manager subscription created');

    // Send subscription to backend
    console.log('[Push] Sending subscription to backend...');
    const subJson = subscription.toJSON();
    console.log('[Push] Subscription endpoint:', subJson.endpoint?.substring(0, 50) + '...');
    
    const result = await api.subscribeToPush(subJson);
    console.log('[Push] Backend response:', result);
    
    console.log('[Push] Successfully subscribed to push notifications');
    return true;
  } catch (error) {
    console.error('[Push] Failed to subscribe:', error);
    console.error('[Push] Error details:', error.response?.data || error.message);
    throw error; // Re-throw so the UI can handle it
  }
};

export const unsubscribeFromPushNotifications = async () => {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
        console.log('Unsubscribed from push notifications');
        return true;
      }
    }
    return false;
  } catch (error) {
    console.error('Failed to unsubscribe from push notifications:', error);
    return false;
  }
};

export const isPushNotificationSupported = () => {
  // Basic checks
  const hasNotification = 'Notification' in window;
  const hasServiceWorker = 'serviceWorker' in navigator;
  const hasPushManager = 'PushManager' in window;
  
  // iOS-specific check - iOS 16.4+ supports push but only for installed PWAs
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                       (window.navigator).standalone === true;
  
  console.log('[Push Support Check]', {
    hasNotification,
    hasServiceWorker,
    hasPushManager,
    isIOS,
    isStandalone,
    userAgent: navigator.userAgent
  });
  
  // On iOS, we need to be in standalone mode (installed PWA)
  // and have service worker support
  if (isIOS) {
    // iOS Safari may not have PushManager on window, but it's available
    // through ServiceWorkerRegistration after installation
    // We return true if in standalone mode with service worker support
    // The actual push subscription will verify full support
    if (isStandalone && hasServiceWorker && hasNotification) {
      return true;
    }
    // Not installed to home screen
    return false;
  }
  
  // Non-iOS browsers need all three
  return hasNotification && hasServiceWorker && hasPushManager;
};

export const subscribeAnonymousDevice = async (customerId) => {
  try {
    // Check if anonymous notifications are enabled for this customer
    const { notification_mode } = await api.getNotificationMode(customerId);
    if (notification_mode !== 'all_devices') {
      console.log('Anonymous notifications not enabled for this customer');
      return false;
    }

    // Request permission first
    const permissionGranted = await requestNotificationPermission();
    if (!permissionGranted) {
      console.log('Notification permission denied');
      return false;
    }

    // Register service worker if not already registered
    let registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      registration = await navigator.serviceWorker.register('/service-worker.js');
      await navigator.serviceWorker.ready;
    }

    // Get VAPID public key from backend
    const { publicKey } = await api.getVapidPublicKey();
    
    // Subscribe to push notifications
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    });

    // Get device ID
    const deviceId = getDeviceId();

    // Send anonymous subscription to backend
    await api.subscribeAnonymousPush({
      ...subscription.toJSON(),
      device_id: deviceId,
      customer_id: customerId
    });
    
    console.log('Successfully subscribed anonymous device to push notifications');
    return true;
  } catch (error) {
    console.error('Failed to subscribe anonymous device:', error);
    return false;
  }
};

export const getDeviceIdForAnalytics = () => {
  return getDeviceId();
};

export const getPushNotificationStatus = async () => {
  if (!isPushNotificationSupported()) {
    return 'unsupported';
  }

  const permission = Notification.permission;
  if (permission === 'denied') {
    return 'denied';
  }

  if (permission === 'granted') {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        return 'subscribed';
      }
    }
    return 'granted';
  }

  return 'default';
};
